import { useQueryClient } from "@tanstack/react-query";

import type { CommonModels } from "@/data/common/common.models";
import { type InvalidateQueryOptions, QueryModule, invalidateQueries } from "@/data/invalidateQueries";
import { useAuthGuardedMutation } from "@/hooks/useAuthGuardedMutation";
import { useAuthGuardedQuery } from "@/hooks/useAuthGuardedQuery";
import type { AppMutationOptions, AppQueryOptions } from "@/types/react-query";

import { BizUserApi } from "./bizUser.api";
import type { BizUserModels } from "./bizUser.models";

export namespace BizUserQueries {
  export const moduleName = QueryModule.BizUser;

  export const keys = {
    all: [moduleName] as const,
    getCurrentUserInfo: () => [...keys.all, "/biz/user/me"] as const,
  };

  /**
   * Query `useGetCurrentUserInfo`
   * @summary Get current user info with organization and role
   * @param { AppQueryOptions } options Query options
   * @returns { UseQueryResult<BizUserModels.GetCurrentUserInfoResponseDto> } Current user info with organization and role
   * @statusCodes [200]
   */
  export const useGetCurrentUserInfo = <TData>(
    options?: AppQueryOptions<typeof BizUserApi.getCurrentUserInfo, TData>,
  ) => {
    return useAuthGuardedQuery({
      queryKey: keys.getCurrentUserInfo(),
      queryFn: BizUserApi.getCurrentUserInfo,
      ...options,
    });
  };

  /**
   * Mutation `useUpdateCurrentUserInfo`
   * @summary Update current user info
   * @param { BizUserModels.UpdateBizUserDto } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CommonModels.BizUserDto> } Updated user info
   * @statusCodes [200]
   */
  export const useUpdateCurrentUserInfo = (
    options?: AppMutationOptions<typeof BizUserApi.updateCurrentUserInfo, { data: BizUserModels.UpdateBizUserDto }> &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizUserApi.updateCurrentUserInfo(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };

  /**
   * Mutation `useRequestUploadProfilePicture`
   * @param { CommonModels.MediaUploadFileRequest } mutation.data Body parameter
   * @param { AppMutationOptions & InvalidateQueryOptions } options Mutation options
   * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> }
   * @statusCodes [201, default]
   */
  export const useRequestUploadProfilePicture = (
    options?: AppMutationOptions<
      typeof BizUserApi.requestUploadProfilePicture,
      { data: CommonModels.MediaUploadFileRequest }
    > &
      InvalidateQueryOptions,
  ) => {
    const queryClient = useQueryClient();

    return useAuthGuardedMutation({
      mutationFn: ({ data }) => BizUserApi.requestUploadProfilePicture(data),
      ...options,
      onSuccess: (...args) => {
        invalidateQueries(queryClient, moduleName, options);
        options?.onSuccess?.(...args);
      },
    });
  };
}
